// The signed live token: otto-app mints it at check-in, the browser sends it
// in `hello` / `resume`, otto-live checks it with no database read (design
// 3.2 row 5, 9.1). HMAC-SHA256 over the payload with a Worker secret.
//
// Format: "v1.<payload base64url>.<signature base64url>"
// Payload: {"cid": consult id, "iat": issued at (unix s), "exp": expiry (unix s)}
//
// Rotation: verify takes a LIST of secrets and accepts any of them, so a new
// secret can be added before the old one is removed (the agency's two secret
// rotation pattern, research 04 section 1.4). Sign always uses the first.
//
// Uses only Web Crypto, TextEncoder, btoa and atob, which Workers, browsers
// and Node all provide. The browser never verifies; it only carries the token.

export const LIVE_TOKEN_TTL_SECONDS = 4 * 60 * 60; // 4 hours (design 3.4 step 2)
const PREFIX = "v1";

export interface LiveTokenClaims {
  /** Consult id. */
  cid: string;
  /** Issued at, unix seconds. */
  iat: number;
  /** Expires at, unix seconds. */
  exp: number;
}

export type LiveTokenFailure = "malformed" | "bad_signature" | "expired" | "wrong_consult" | "no_secret";

export type LiveTokenResult = { ok: true; claims: LiveTokenClaims } | { ok: false; reason: LiveTokenFailure };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> | null {
  if (!/^[A-Za-z0-9_-]*$/.test(text)) return null;
  const padded = text.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((text.length + 3) % 4);
  try {
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

async function hmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", encoder.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, [
    "sign",
    "verify",
  ]);
}

/** Mint a token. `secrets[0]` signs; pass the same list the verifier uses. */
export async function signLiveToken(
  input: { consultId: string; now?: Date; ttlSeconds?: number },
  secrets: readonly string[],
): Promise<string> {
  const secret = secrets[0];
  if (!secret) throw new Error("signLiveToken: no secret configured (LIVE_TOKEN_SECRET)");
  const iat = Math.floor((input.now ?? new Date()).getTime() / 1000);
  const claims: LiveTokenClaims = { cid: input.consultId, iat, exp: iat + (input.ttlSeconds ?? LIVE_TOKEN_TTL_SECONDS) };
  const payload = toBase64Url(encoder.encode(JSON.stringify(claims)));
  const signed = `${PREFIX}.${payload}`;
  const signature = new Uint8Array(await crypto.subtle.sign("HMAC", await hmacKey(secret), encoder.encode(signed)));
  return `${signed}.${toBase64Url(signature)}`;
}

/**
 * Check a token for one consult. Signature first (constant time through
 * crypto.subtle.verify), then expiry, then the consult id.
 */
export async function verifyLiveToken(
  token: string,
  secrets: readonly string[],
  expect: { consultId: string; now?: Date },
): Promise<LiveTokenResult> {
  const usable = secrets.filter((s) => s.length > 0);
  if (usable.length === 0) return { ok: false, reason: "no_secret" };

  const parts = token.split(".");
  if (parts.length !== 3 || parts[0] !== PREFIX) return { ok: false, reason: "malformed" };
  const [prefix, payloadPart, signaturePart] = parts as [string, string, string];
  const signature = fromBase64Url(signaturePart);
  const payloadBytes = fromBase64Url(payloadPart);
  if (!signature || !payloadBytes) return { ok: false, reason: "malformed" };

  const signed = encoder.encode(`${prefix}.${payloadPart}`);
  let valid = false;
  for (const secret of usable) {
    if (await crypto.subtle.verify("HMAC", await hmacKey(secret), signature, signed)) {
      valid = true;
      break;
    }
  }
  if (!valid) return { ok: false, reason: "bad_signature" };

  let claims: LiveTokenClaims;
  try {
    const parsed = JSON.parse(decoder.decode(payloadBytes)) as Partial<LiveTokenClaims>;
    if (typeof parsed.cid !== "string" || typeof parsed.iat !== "number" || typeof parsed.exp !== "number") {
      return { ok: false, reason: "malformed" };
    }
    claims = { cid: parsed.cid, iat: parsed.iat, exp: parsed.exp };
  } catch {
    return { ok: false, reason: "malformed" };
  }

  const nowSeconds = Math.floor((expect.now ?? new Date()).getTime() / 1000);
  if (claims.exp <= nowSeconds) return { ok: false, reason: "expired" };
  if (claims.cid !== expect.consultId) return { ok: false, reason: "wrong_consult" };
  return { ok: true, claims };
}

/** Split a comma separated secret list from an env var ("new,old"). */
export function secretList(raw: string | undefined): string[] {
  return (raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
